import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import {
  Users,
  TrendingUp,
  Key,
  Activity,
  DollarSign,
  UserCheck,
  UserX,
  CreditCard,
  BarChart3,
  LogOut,
  RefreshCw
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

function ConsoleDashboard() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();

  const fetchStats = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('consoleToken');

      if (!token) {
        navigate('/console/login');
        return;
      }

      const response = await api.get('/console/dashboard/stats', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setStats(response.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('consoleToken');
        navigate('/console/login');
      } else {
        setError(err.response?.data?.message || 'Failed to fetch statistics');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async (page = 1) => {
    try {
      const token = localStorage.getItem('consoleToken');

      if (!token) {
        navigate('/console/login');
        return;
      }

      const response = await api.get(`/console/dashboard/users?page=${page}&limit=10`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setUsers(response.data.users);
      setCurrentPage(response.data.pagination.page);
      setTotalPages(response.data.pagination.totalPages);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('consoleToken');
        navigate('/console/login');
      }
    }
  };

  useEffect(() => {
    fetchStats();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('consoleToken');
    navigate('/console/login');
  };

  const handleRefresh = () => {
    fetchStats();
    fetchUsers(currentPage);
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Snaptura Console</h1>
              <p className="text-sm text-gray-600 mt-1">System Overview & Analytics</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleRefresh}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </button>
              <button
                onClick={handleLogout}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        </div>
      )}

      {stats && (
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Total Users */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <span className="text-sm text-green-600 font-medium">
                  +{stats.signups.last7Days} this week
                </span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">{stats.users.total}</h3>
              <p className="text-sm text-gray-600">Total Users</p>
              <div className="mt-2 text-xs text-gray-500">
                {stats.users.verified} verified ({stats.users.verificationRate}%)
              </div>
            </div>

            {/* Revenue */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
                <span className="text-sm text-gray-600 font-medium">
                  {stats.users.paid} paid users
                </span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">{stats.revenue.estimatedMRR}</h3>
              <p className="text-sm text-gray-600">Monthly Recurring Revenue</p>
              <div className="mt-2 text-xs text-gray-500">
                {stats.revenue.avgRevenuePerUser} avg per user
              </div>
            </div>

            {/* API Usage */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Activity className="w-6 h-6 text-purple-600" />
                </div>
                <span className="text-sm text-gray-600 font-medium">
                  {stats.apiUsage.activeUsers} active
                </span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">
                {stats.apiUsage.totalCalls.toLocaleString()}
              </h3>
              <p className="text-sm text-gray-600">API Calls This Month</p>
              <div className="mt-2 text-xs text-gray-500">
                {stats.apiUsage.avgPerUser} avg per user
              </div>
            </div>

            {/* API Keys */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <Key className="w-6 h-6 text-orange-600" />
                </div>
                <span className="text-sm text-gray-600 font-medium">
                  {stats.apiKeys.active} active
                </span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">{stats.apiKeys.total}</h3>
              <p className="text-sm text-gray-600">Total API Keys</p>
              <div className="mt-2 text-xs text-gray-500">
                {stats.apiKeys.revoked} revoked
              </div>
            </div>
          </div>

          {/* Secondary Metrics */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* User Status */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <UserCheck className="w-5 h-5 mr-2" />
                User Status
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Active Users</span>
                  <span className="text-lg font-semibold text-green-600">{stats.users.active}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Inactive Users</span>
                  <span className="text-lg font-semibold text-red-600">{stats.users.inactive}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Verified Email</span>
                  <span className="text-lg font-semibold text-blue-600">{stats.users.verified}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Unverified Email</span>
                  <span className="text-lg font-semibold text-yellow-600">{stats.users.unverified}</span>
                </div>
              </div>
            </div>

            {/* Plans Distribution */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <CreditCard className="w-5 h-5 mr-2" />
                Plans Distribution
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Free</span>
                  <span className="text-lg font-semibold text-gray-700">{stats.plans.free}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Starter ($9/mo)</span>
                  <span className="text-lg font-semibold text-blue-600">{stats.plans.starter}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Growth ($29/mo)</span>
                  <span className="text-lg font-semibold text-purple-600">{stats.plans.growth}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Pro ($79/mo)</span>
                  <span className="text-lg font-semibold text-orange-600">{stats.plans.pro}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Signups */}
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <TrendingUp className="w-5 h-5 mr-2" />
              Recent Signups
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border-l-4 border-green-500 pl-4">
                <div className="text-3xl font-bold text-gray-900">{stats.signups.today}</div>
                <div className="text-sm text-gray-600">Today</div>
              </div>
              <div className="border-l-4 border-blue-500 pl-4">
                <div className="text-3xl font-bold text-gray-900">{stats.signups.last7Days}</div>
                <div className="text-sm text-gray-600">Last 7 Days</div>
              </div>
            </div>
          </div>

          {/* Monthly Signups Chart */}
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2" />
              Monthly User Signups (Last 12 Months)
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={stats.signups.monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 12 }}
                  stroke="#888888"
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  stroke="#888888"
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                  labelStyle={{ color: '#374151', fontWeight: 'bold' }}
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#6366f1"
                  strokeWidth={3}
                  dot={{ fill: '#6366f1', r: 4 }}
                  activeDot={{ r: 6 }}
                  name="Users"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Recent Users Table */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <BarChart3 className="w-5 h-5 mr-2" />
                Recent Users
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Plan
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      API Usage
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          user.plan === 'free' ? 'bg-gray-100 text-gray-800' :
                          user.plan === 'starter' ? 'bg-blue-100 text-blue-800' :
                          user.plan === 'growth' ? 'bg-purple-100 text-purple-800' :
                          'bg-orange-100 text-orange-800'
                        }`}>
                          {user.plan.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          {user.emailVerified ? (
                            <span className="text-xs text-green-600 flex items-center">
                              <UserCheck className="w-3 h-3 mr-1" /> Verified
                            </span>
                          ) : (
                            <span className="text-xs text-yellow-600 flex items-center">
                              <UserX className="w-3 h-3 mr-1" /> Unverified
                            </span>
                          )}
                          {user.isActive ? (
                            <span className="text-xs text-green-600">Active</span>
                          ) : (
                            <span className="text-xs text-red-600">Inactive</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.apiUsage.toLocaleString()} / {user.monthlyQuota.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
                <button
                  onClick={() => fetchUsers(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => fetchUsers(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="mt-8 text-center text-sm text-gray-500">
            Last updated: {new Date(stats.timestamp).toLocaleString()}
          </div>
        </main>
      )}
    </div>
  );
}

export default ConsoleDashboard;
